import { useRef, useState, useCallback } from 'react';
import { useScrollAnimation, getStaggerProps, animationClasses } from '@/hooks/useScrollAnimation';
import { latestBooks } from '@/mocks/homeData';
import { latestBrandReleases } from '../../../../../shared/brand-latest/latestReleases';
import { getBookPublicationBadge, getBookPublicationBadgeClass, getBookPublicationBadgeLabel } from '../../../../../shared/book-publication-status';

interface Book {
  id: string;
  cover: string;
  title: string;
  author: string;
  publishDate: string;
  tags: string[];
  summary: string;
}

function getBookTopic(tags: string[]): string | null {
  const lowerTags = tags.map((t) => t.toLowerCase());

  if (
    lowerTags.includes('心理學')
    || lowerTags.includes('職場心理')
    || lowerTags.includes('性格分析')
    || lowerTags.includes('健康心理')
  ) {
    if (lowerTags.includes('健康心理')) return '情緒與人際關係';
    if (lowerTags.includes('性格分析')) return '大眾心理學';
    return '大眾心理學';
  }

  if (lowerTags.includes('歷史智慧')) {
    if (lowerTags.includes('職場策略')) return '以古諷今';
    return '歷史與人文思考';
  }

  const businessTags = ['商業書', '管理思維', '職場收入', '經濟趨勢', '金融史', '投資理財', '財經知識', '金錢思維', '消費心理'];
  if (businessTags.some((t) => lowerTags.includes(t))) {
    if (lowerTags.includes('管理思維')) return '管理與職場';
    if (lowerTags.includes('職場收入')) return '管理與職場';
    return '商業理財';
  }

  return null;
}

function getTopicStyle(topic: string): string {
  if (topic === '商業理財' || topic === '管理與職場' || topic === '商業思考') {
    return 'bg-primary-100 text-primary-700';
  }
  if (topic === '大眾心理學' || topic === '情緒與人際關係') {
    return 'bg-accent-100 text-accent-700';
  }
  return 'bg-secondary-100 text-secondary-700';
}

function BookDetailModal({ book, onClose }: { book: Book; onClose: () => void }) {
  if (!book) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${book.title} 書籍資訊`}
    >
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg bg-background-50 rounded-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors"
          aria-label="關閉"
        >
          <i className="ri-close-line text-foreground-600" />
        </button>

        <div className="flex flex-col sm:flex-row">
          <div className="w-full sm:w-40 flex-shrink-0 p-4 sm:p-5 flex justify-center sm:justify-start">
            <div className="w-28 sm:w-full aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
              <img
                src={book.cover}
                alt={book.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="flex-1 p-4 sm:p-5 sm:pl-0">
            <h3 className="font-heading text-base font-bold text-foreground-900 leading-snug mb-2">
              {book.title}
            </h3>
            <p className="text-sm text-foreground-600 mb-3">{book.author}</p>

            <div className="flex flex-wrap gap-1.5 mb-3">
              {book.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block px-2 py-0.5 rounded-md text-xs bg-background-100 text-foreground-500"
                >
                  {tag}
                </span>
              ))}
            </div>

            <p className="text-xs text-foreground-400 mb-1">
              ISBN：
              {book.id}
            </p>
            <p className="text-xs text-foreground-400 mb-3">
              出版日期：
              {book.publishDate}
            </p>

            <p className="text-sm text-foreground-600 leading-relaxed">{book.summary}</p>

            <div className="mt-4 pt-3 border-t border-background-200/70">
              <p className="text-xs text-foreground-400 mb-2">
                完整書名、作者及相關出版資訊，以崧博書籍資料庫為準。
              </p>
              <a
                href="https://catalog.sonbook.net/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                前往書籍資料庫
                <i className="ri-external-link-line" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LatestBooks2026Section() {
  const { ref, isVisible } = useScrollAnimation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);

  const books2026 = [
    ...latestBrandReleases
      .filter((book) => book.brandSlug === 'finance-line')
      .map((book) => ({
        id: book.isbn,
        cover: book.coverImage,
        title: book.title,
        author: book.author,
        publishDate: book.publishDate,
        tags: [book.category],
        summary: book.summary,
      })),
    ...latestBooks.filter((book) => book.publishDate.startsWith('2026')),
  ];
  const hasBooks = books2026.length > 0;

  const dragState = useRef({ startX: 0, scrollLeft: 0, isDown: false });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    dragState.current.isDown = true;
    dragState.current.startX = e.pageX - el.offsetLeft;
    dragState.current.scrollLeft = el.scrollLeft;
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el || !dragState.current.isDown) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = (x - dragState.current.startX) * 1.2;
    el.scrollLeft = dragState.current.scrollLeft - walk;
  }, []);

  const handleMouseUp = useCallback(() => {
    dragState.current.isDown = false;
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    dragState.current.isDown = false;
    setIsDragging(false);
  }, []);

  return (
    <section ref={ref} className="py-12 md:py-16 bg-background-100">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Title */}
          <div className={`text-center mb-8 md:mb-10 ${animationClasses.base} ${isVisible ? animationClasses.visible : animationClasses.hidden}`}>
            <span className="inline-block text-xs font-medium text-accent-600 tracking-wider uppercase mb-3">
              最新出版
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 tracking-tight">
              2026 最新發行
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              探索財經錢線於 2026 年最新發行的商業理財、心理學與人文思考書籍。
            </p>
          </div>

          {hasBooks ? (
            <>
              <div className="relative">
                {/* Left fade */}
                <div className="absolute left-0 top-0 bottom-0 w-12 md:w-20 bg-gradient-to-r from-background-100 via-background-100/80 to-transparent z-10 pointer-events-none rounded-l-lg" />

                {/* Right fade */}
                <div className="absolute right-0 top-0 bottom-0 w-12 md:w-20 bg-gradient-to-l from-background-100 via-background-100/80 to-transparent z-10 pointer-events-none rounded-r-lg" />

                {/* Scrollable track */}
                <div
                  ref={scrollRef}
                  className={`flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 ${
                    isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'
                  }`}
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                >
                  {books2026.map((book, i) => {
                    const topic = getBookTopic(book.tags);
                    return (
                      <div
                        key={book.id}
                        {...getStaggerProps(isVisible, i, 100)}
                        className="group flex-shrink-0 flex flex-col snap-start w-[75%] sm:w-[45%] md:w-[30%] lg:w-[22%]"
                      >
                        {/* Cover */}
                        <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                          <img
                            src={book.cover}
                            alt={book.title}
                            className="w-full h-full object-cover"
                            draggable={false}
                          />
                          {(() => {
                            const badge = getBookPublicationBadge(book.publishDate);
                            return badge ? (
                              <span className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-xs font-medium ${getBookPublicationBadgeClass(badge)}`}>
                                {getBookPublicationBadgeLabel(badge)}
                              </span>
                            ) : null;
                          })()}
                        </div>

                        {/* Topic badge */}
                        {topic && (
                          <span className={`self-start inline-block px-2 py-0.5 rounded-md text-xs font-medium mb-2 ${getTopicStyle(topic)}`}>
                            {topic}
                          </span>
                        )}

                        {/* Title */}
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1 leading-snug">
                          {book.title}
                        </h3>

                        {/* Author */}
                        <p className="text-xs text-foreground-500 mb-3">{book.author}</p>

                        {/* Info button */}
                        <button
                          onClick={() => setSelectedBook(book)}
                          className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-book-open-line" />
                          查看書籍資訊
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bottom catalog CTA */}
              <div {...getStaggerProps(isVisible, Math.min(books2026.length, 6), 100)} className="mt-10 md:mt-12 text-center">
                <p className="text-sm text-foreground-500 mb-4 max-w-xl mx-auto leading-relaxed">
                  完整書名、作者及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
                </p>
                <a
                  href="https://catalog.sonbook.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  前往書籍資料庫
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </>
          ) : (
            <div {...getStaggerProps(isVisible, 0, 200)} className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5 rounded-xl bg-primary-50">
                <i className="ri-book-2-line text-primary-400 text-2xl" />
              </div>
              <p className="text-foreground-500 text-sm md:text-base">
                目前尚無可顯示的 2026 最新發行書籍。
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Book detail modal */}
      {selectedBook && (
        <BookDetailModal book={selectedBook} onClose={() => setSelectedBook(null)} />
      )}
    </section>
  );
}
